import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';

interface Document {
  id: string;
  documentType: string;
  fileName: string;
  counterpartyName: string;
  assetName?: string;
  contractNumber?: string;
  status: string;
  uploadedAt: string;
  uploadedBy: string;
}

export default function DocumentsPage() {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const [items, setItems] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const inputBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const tableBorder = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const separator = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const iconBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const selectBg = isDark ? '#1A1A1A' : '#fff';

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const data = await api.get<Document[]>('/documents');
      setItems(data);
    } catch (err) {
      const isApiError = err instanceof ApiError || (err as { name?: string })?.name === 'ApiError';
      const status = (err as { status?: number })?.status;
      if (isApiError && status === 404) {
        setItems([]);
      } else {
        const message = getApiErrorMessage(err, 'Failed to load documents');
        addToast(message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const filtered = items.filter((d) =>
    d.fileName.toLowerCase().includes(search.toLowerCase()) ||
    d.counterpartyName.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    const map: Record<string, { bg: string; color: string }> = {
      verified: { bg: 'rgba(13,122,60,0.1)', color: '#0D7A3C' },
      pending: { bg: 'rgba(184,134,11,0.1)', color: '#B8860B' },
      rejected: { bg: 'rgba(209,34,41,0.1)', color: '#D12229' },
      draft: { bg: 'rgba(107,107,112,0.1)', color: '#6B6B70' },
    };
    const style = map[status] ?? map.draft;
    return (
      <span className="text-xs font-medium px-2.5 py-1 rounded-full capitalize" style={{ background: style.bg, color: style.color }}>
        {status}
      </span>
    );
  };

  return (
    <DashboardLayout breadcrumb={[t('documents_title')]}>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>
            {t('documents_title')}
          </h1>
          <p className="text-sm mt-1" style={{ color: textMuted }}>
            {t('documents_subtitle')}
          </p>
        </div>
      </div>

      <div className="rounded-xl" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
        <div className="flex items-center gap-3 p-4" style={{ borderBottom: `1px solid ${separator}` }}>
          <div className="flex items-center gap-2 px-3 py-2 rounded-md flex-1 max-w-md" style={{ background: inputBg, border: `1px solid ${inputBorder}` }}>
            <i className="ri-search-line text-sm" style={{ color: textMuted }}></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('documents_search')}
              className="bg-transparent outline-none text-sm flex-1"
              style={{ color: textMain }}
            />
          </div>
          <button
            onClick={loadDocuments}
            className="w-9 h-9 flex items-center justify-center rounded-md cursor-pointer transition-colors"
            style={{ border: `1px solid ${inputBorder}`, background: selectBg }}
            title="Refresh"
          >
            <i className="ri-refresh-line text-sm" style={{ color: textMuted }}></i>
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <i className="ri-loader-4-line animate-spin text-2xl" style={{ color: textMuted }}></i>
            <p className="text-sm mt-3" style={{ color: textMuted }}>{t('documents_loading')}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: iconBg }}>
              <i className="ri-folder-line text-xl" style={{ color: textMuted }}></i>
            </div>
            <p className="text-sm font-medium" style={{ color: textMain }}>{t('documents_not_found')}</p>
            <p className="text-xs mt-1" style={{ color: textMuted }}>
              {search ? t('documents_filter_hint') : t('documents_not_found_hint')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: tableHeaderBg }}>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('documents_col_file')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('documents_col_type')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('documents_col_counterparty')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('documents_col_asset_contract')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('documents_col_status')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('documents_col_uploaded')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: textMuted }}>{t('documents_col_by')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => (
                  <tr key={d.id} style={{ borderBottom: `1px solid ${tableBorder}` }}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <i className="ri-file-text-line text-lg" style={{ color: '#D12229' }}></i>
                        <span className="font-medium" style={{ color: textMain }}>{d.fileName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 capitalize" style={{ color: textMuted }}>{d.documentType.replace('_', ' ')}</td>
                    <td className="px-4 py-3" style={{ color: textMain }}>{d.counterpartyName}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: textMuted }}>
                      {d.assetName ?? d.contractNumber ?? '—'}
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(d.status)}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: textMuted }}>
                      {new Date(d.uploadedAt).toLocaleDateString('uk-UA')}
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: textMuted }}>{d.uploadedBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}